import { useMemo } from 'react';
import { PresentationTreeNetwork, TaxonomyChild } from 'entities/tag';

export interface SearchEntry {
  nodeId: string;
  searchText: string;
  networkIdx: number;
  ancestorIds: string[];
  descendantIds: string[];
}

// Collects a subtree into `entries` and returns the ids of every node within it
// (the passed-in children plus all of their descendants), so each entry can carry
// the full set of descendant ids beneath it.
function collectNodes(children: TaxonomyChild[], networkIdx: number, networkDefinition: string, ancestorPath: string[], entries: SearchEntry[]): string[] {
  const subtreeIds: string[] = [];
  for (const child of children) {
    const parts: string[] = [child.id, networkDefinition];

    if (child.labels) {
      for (const lang of Object.keys(child.labels)) {
        const langLabels = child.labels[lang];
        for (const type of Object.keys(langLabels)) {
          parts.push(langLabels[type]);
        }
      }
    }

    const entry: SearchEntry = {
      nodeId: child.id,
      searchText: parts.join(' ').toLowerCase(),
      networkIdx,
      ancestorIds: ancestorPath,
      descendantIds: [],
    };
    entries.push(entry);

    if (child.children?.length > 0) {
      entry.descendantIds = collectNodes(child.children, networkIdx, networkDefinition, [...ancestorPath, child.id], entries);
    }

    subtreeIds.push(child.id, ...entry.descendantIds);
  }
  return subtreeIds;
}

export function buildSearchIndex(networks: PresentationTreeNetwork[]): SearchEntry[] {
  const entries: SearchEntry[] = [];
  for (let i = 0; i < networks.length; i++) {
    const network = networks[i];
    const definition = network.linkrole.definition;
    const linkroleTexts: string[] = [definition];
    if (network.linkrole.labels) {
      for (const lang of Object.keys(network.linkrole.labels)) {
        const text = network.linkrole.labels[lang];
        if (text) linkroleTexts.push(text);
      }
    }
    const networkSearchText = linkroleTexts.join(' ');
    for (const root of network.roots) {
      // Index the root node itself
      const rootParts: string[] = [root.id, networkSearchText];
      if (root.labels) {
        for (const lang of Object.keys(root.labels)) {
          const langLabels = root.labels[lang];
          for (const type of Object.keys(langLabels)) {
            rootParts.push(langLabels[type]);
          }
        }
      }
      const rootEntry: SearchEntry = {
        nodeId: root.id,
        searchText: rootParts.join(' ').toLowerCase(),
        networkIdx: i,
        ancestorIds: [],
        descendantIds: [],
      };
      entries.push(rootEntry);
      rootEntry.descendantIds = collectNodes(root.children, i, networkSearchText, [], entries);
    }
  }
  return entries;
}

export interface TaxonomySearchResult {
  filteredNetworkIndices: Set<number> | null;
  searchExpandIdsByNetwork: Map<number, Set<string>> | null;
  searchVisibleIdsByNetwork: Map<number, Set<string>> | null;
  isSearching: boolean;
}

export function useTaxonomySearch(networks: PresentationTreeNetwork[] | undefined, debouncedSearch: string): TaxonomySearchResult {
  const index = useMemo(() => buildSearchIndex(networks ?? []), [networks]);

  // networkIdx -> nodeId -> every descendant id beneath that node (unioned across
  // duplicate occurrences of a concept within the same network).
  const descendantsByNode = useMemo(() => {
    const map = new Map<number, Map<string, Set<string>>>();
    for (const entry of index) {
      let byNode = map.get(entry.networkIdx);
      if (!byNode) {
        byNode = new Map<string, Set<string>>();
        map.set(entry.networkIdx, byNode);
      }
      let set = byNode.get(entry.nodeId);
      if (!set) {
        set = new Set<string>();
        byNode.set(entry.nodeId, set);
      }
      for (const descendantId of entry.descendantIds) set.add(descendantId);
    }
    return map;
  }, [index]);

  return useMemo(() => {
    if (!debouncedSearch.trim()) {
      return { filteredNetworkIndices: null, searchExpandIdsByNetwork: null, searchVisibleIdsByNetwork: null, isSearching: false };
    }

    const terms = debouncedSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const matches = index.filter((entry) => terms.every((t) => entry.searchText.includes(t)));

    const filteredNetworkIndices = new Set<number>();
    const searchExpandIdsByNetwork = new Map<number, Set<string>>();
    const searchVisibleIdsByNetwork = new Map<number, Set<string>>();

    const getOrCreate = (m: Map<number, Set<string>>, idx: number) => {
      let s = m.get(idx);
      if (!s) {
        s = new Set<string>();
        m.set(idx, s);
      }
      return s;
    };

    for (const match of matches) {
      filteredNetworkIndices.add(match.networkIdx);
      const visible = getOrCreate(searchVisibleIdsByNetwork, match.networkIdx);
      const byNode = descendantsByNode.get(match.networkIdx);
      visible.add(match.nodeId);
      for (const ancestorId of match.ancestorIds) {
        getOrCreate(searchExpandIdsByNetwork, match.networkIdx).add(ancestorId);
        visible.add(ancestorId);
        // Reveal every sibling branch along the path: each ancestor exposes all of
        // its children and their full subtrees (collapsed, but browsable).
        const ancestorSubtree = byNode?.get(ancestorId);
        if (ancestorSubtree) for (const id of ancestorSubtree) visible.add(id);
      }
      // Keep the matched node's own subtree visible (covers a match with no ancestors).
      for (const descendantId of match.descendantIds) {
        visible.add(descendantId);
      }
    }

    return { filteredNetworkIndices, searchExpandIdsByNetwork, searchVisibleIdsByNetwork, isSearching: true };
  }, [index, descendantsByNode, debouncedSearch]);
}
